/**
 * Tracks conversion funnel: create → share → view → signup → activate
 */

import { isDocumentTelemetryEligible } from '@/lib/privacy/eligibility';
import { containsCapabilityToken, scrubTelemetryString, scrubTelemetryValue } from '@/lib/privacy/scrub';
import { supabase } from '@/lib/supabase/client';

export interface CollectionEvent {
  event_type: 
    | 'collection_created'
    | 'collection_shared'
    | 'collection_viewed'
    | 'collection_signup'
    | 'collection_save'
    | 'venue_saved_from_collection';
  collection_id?: string;
  share_token?: string;
  source?: 'whatsapp' | 'instagram' | 'copy' | 'direct' | 'native';
  user_id?: string;
  session_id?: string;
  metadata?: {
    venue_count?: number;
    destination?: string;
    curator_id?: string;
    viewer_authenticated?: boolean;
    venues_shown?: number;
    venues_hidden?: number;
    referrer?: string;
  };
  timestamp: Date;
}

export interface ConversionFunnel {
  collections_created: number;
  collections_shared: number;
  total_views: number;
  unique_viewers: number;
  signups: number;
  activations: number; // First save after signup
  share_rate: number; // shared/created
  view_rate: number; // views/shares
  signup_rate: number; // signups/views
  activation_rate: number; // activations/signups
}

class CollectionAnalyticsService {
  private eventQueue: CollectionEvent[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private sessionId: string;
  
  constructor() {
    // Generate session ID for anonymous tracking
    this.sessionId = this.getOrCreateSessionId();
  }
  
  private getOrCreateSessionId(): string {
    const stored = localStorage.getItem('analytics_session_id');
    if (stored) return stored;
    
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('analytics_session_id', newId);
    return newId;
  }
  
  trackCollectionCreated(
    collection_id: string,
    destination: string,
    venue_count: number,
    user_id?: string
  ): void {
    this.trackEvent({
      event_type: 'collection_created',
      collection_id,
      user_id,
      session_id: this.sessionId,
      metadata: {
        destination,
        venue_count
      },
      timestamp: new Date()
    });
  }
  
  trackCollectionShared(
    collection_id: string,
    share_token: string,
    source: CollectionEvent['source'],
    user_id?: string
  ): void {
    const usableId = collection_id && collection_id !== share_token ? collection_id : undefined;
    this.trackEvent({
      event_type: 'collection_shared',
      collection_id: usableId,
      source,
      user_id,
      session_id: this.sessionId,
      timestamp: new Date()
    });
  }
  
  trackCollectionViewed(
    share_token: string,
    metadata: {
      destination: string;
      curator_id?: string;
      viewer_authenticated: boolean;
      venues_shown: number;
      venues_hidden: number;
    },
    source?: string
  ): void {
    void share_token;
    const referrer = typeof document === 'undefined' ? '' : document.referrer || '';
    this.trackEvent({
      event_type: 'collection_viewed',
      source: (source || 'direct') as CollectionEvent['source'],
      session_id: this.sessionId,
      metadata: {
        ...metadata,
        referrer: containsCapabilityToken(referrer) ? '' : scrubTelemetryString(referrer),
      },
      timestamp: new Date()
    });
  }
  
  trackCollectionSignup(user_id: string): void {
    this.trackEvent({
      event_type: 'collection_signup',
      user_id,
      session_id: this.sessionId,
      timestamp: new Date()
    });
  }
  
  /**
   * Track venue saved from collection (activation)
   */
  trackVenueSaved(
    venue_id: string,
    collection_token?: string,
    user_id?: string
  ): void {
    void venue_id;
    void collection_token;
    this.trackEvent({
      event_type: 'venue_saved_from_collection',
      user_id,
      session_id: this.sessionId,
      metadata: {
        venue_count: 1
      },
      timestamp: new Date()
    });
  }
  
  private trackEvent(event: CollectionEvent): void {
    if (import.meta.env.VITE_ANALYTICS_ENABLED !== '1') return;
    if (!isDocumentTelemetryEligible()) return;
    const safeEvent: CollectionEvent = {
      ...event,
      share_token: undefined,
      metadata: event.metadata ? scrubTelemetryValue(event.metadata) : undefined,
    };
    this.eventQueue.push(safeEvent);
    
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => this.flushEvents(), 2000);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Collection Event:', {
        type: event.event_type,
        source: event.source,
        metadata: event.metadata
      });
    }
  }
  
  private async flushEvents(): Promise<void> {
    if (this.eventQueue.length === 0) return;
    
    const events = [...this.eventQueue];
    this.eventQueue = [];
    
    try {
      const records = events.map(e => ({
        event_type: e.event_type,
        collection_id: e.collection_id,
        source: e.source,
        user_id: e.user_id,
        session_id: e.session_id,
        metadata: e.metadata,
        created_at: e.timestamp.toISOString()
      }));
      
      await supabase
        .from('collection_analytics')
        .insert(records);
        
      console.log(`📊 Flushed ${records.length} analytics events`);
    } catch (error) {
      console.error('Failed to flush analytics:', error);
      // Re-add to queue for retry
      this.eventQueue.unshift(...events);
    }
  }
  
  async getConversionFunnel(
    start_date?: Date,
    end_date?: Date
  ): Promise<ConversionFunnel> {
    const startDate = start_date || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 days
    const endDate = end_date || new Date();
    
    try {
      const { data } = await supabase
        .from('collection_analytics')
        .select('event_type, session_id, user_id')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
      
      if (!data) {
        return this.emptyFunnel();
      }
      
      const created = data.filter(e => e.event_type === 'collection_created').length;
      const shared = data.filter(e => e.event_type === 'collection_shared').length;
      const views = data.filter(e => e.event_type === 'collection_viewed').length;
      const uniqueViewers = new Set(
        data.filter(e => e.event_type === 'collection_viewed').map(e => e.session_id)
      ).size;
      const signups = data.filter(e => e.event_type === 'collection_signup').length;
      const activations = data.filter(e => e.event_type === 'venue_saved_from_collection').length;
      
      return {
        collections_created: created,
        collections_shared: shared,
        total_views: views,
        unique_viewers: uniqueViewers,
        signups,
        activations,
        share_rate: created > 0 ? shared / created : 0,
        view_rate: shared > 0 ? views / shared : 0,
        signup_rate: views > 0 ? signups / views : 0,
        activation_rate: signups > 0 ? activations / signups : 0
      };
    } catch (error) {
      console.error('Failed to get conversion funnel:', error);
      return this.emptyFunnel();
    }
  }
  
  async getPerformanceBySource(): Promise<Record<string, ConversionFunnel>> {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    
    try {
      const { data } = await supabase
        .from('collection_analytics')
        .select('event_type, source, session_id, user_id')
        .gte('created_at', thirtyDaysAgo.toISOString());
      
      if (!data) return {};
      
      const sources = ['whatsapp', 'instagram', 'copy', 'direct', 'native'];
      const result: Record<string, ConversionFunnel> = {};
      
      for (const source of sources) {
        const sourceData = data.filter(e => e.source === source);
        if (sourceData.length === 0) continue;
        
        const shared = sourceData.filter(e => e.event_type === 'collection_shared').length;
        const views = sourceData.filter(e => e.event_type === 'collection_viewed').length;
        const signups = sourceData.filter(e => e.event_type === 'collection_signup').length;
        
        result[source] = {
          collections_created: 0, // Not source-specific
          collections_shared: shared,
          total_views: views,
          unique_viewers: new Set(
            sourceData.filter(e => e.event_type === 'collection_viewed').map(e => e.session_id)
          ).size,
          signups,
          activations: sourceData.filter(e => e.event_type === 'venue_saved_from_collection').length,
          share_rate: 0,
          view_rate: shared > 0 ? views / shared : 0,
          signup_rate: views > 0 ? signups / views : 0,
          activation_rate: 0
        };
      }
      
      return result;
    } catch (error) {
      console.error('Failed to get performance by source:', error);
      return {};
    }
  }
  
  private emptyFunnel(): ConversionFunnel {
    return {
      collections_created: 0,
      collections_shared: 0,
      total_views: 0,
      unique_viewers: 0,
      signups: 0,
      activations: 0,
      share_rate: 0,
      view_rate: 0,
      signup_rate: 0,
      activation_rate: 0
    };
  }
}

export const collectionAnalytics = new CollectionAnalyticsService();